import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_TITLE = 'Action-Humanitaire Bénin';

// Map des routes vers leurs titres FR/EN
const PAGE_TITLES = {
  // Accueil
  '/fr':        { fr: 'Accueil',                          en: 'Home' },
  '/en':        { fr: 'Accueil',                          en: 'Home' },

  // À propos
  '/fr/about':       { fr: 'À propos',                   en: 'About Us' },
  '/en/about':       { fr: 'À propos',                   en: 'About Us' },
  '/fr/mission':     { fr: 'Notre Mission',               en: 'Our Mission' },
  '/en/mission':     { fr: 'Notre Mission',               en: 'Our Mission' },
  '/fr/historique':  { fr: 'Notre Histoire',              en: 'Our History' },
  '/en/historique':  { fr: 'Notre Histoire',              en: 'Our History' },
  '/fr/equipe':      { fr: 'Notre Équipe',                en: 'Our Team' },
  '/en/equipe':      { fr: 'Notre Équipe',                en: 'Our Team' },

  // Projets
  '/fr/projets':     { fr: 'Nos Projets',                 en: 'Our Projects' },
  '/en/projets':     { fr: 'Nos Projets',                 en: 'Our Projects' },
  '/fr/projet/sante-et-securite':      { fr: 'Santé et Sécurité',        en: 'Health and Safety' },
  '/en/projet/sante-et-securite':      { fr: 'Santé et Sécurité',        en: 'Health and Safety' },
  '/fr/projet/eau-et-assainissement':  { fr: 'Eau et Assainissement',    en: 'Water and Sanitation' },
  '/en/projet/eau-et-assainissement':  { fr: 'Eau et Assainissement',    en: 'Water and Sanitation' },
  '/fr/projet/environnement':          { fr: 'Environnement',            en: 'Environment' },
  '/en/projet/environnement':          { fr: 'Environnement',            en: 'Environment' },

  // Actualités
  '/fr/actualites':  { fr: 'Actualités',                  en: 'News' },
  '/en/actualites':  { fr: 'Actualités',                  en: 'News' },

  // Développement
  '/fr/developpement': { fr: 'Développement',             en: 'Development' },
  '/en/developpement': { fr: 'Développement',             en: 'Development' },

  // Publications
  '/fr/publications':  { fr: 'Publications',              en: 'Publications' },
  '/en/publications':  { fr: 'Publications',              en: 'Publications' },

  // Documents
  '/fr/documents':   { fr: 'Documents',                   en: 'Documents' },
  '/en/documents':   { fr: 'Documents',                   en: 'Documents' },

  // Adhésion
  '/fr/adhesion':    { fr: 'Adhésion',                    en: 'Membership' },
  '/en/adhesion':    { fr: 'Adhésion',                    en: 'Membership' },

  // Don
  '/fr/don':         { fr: 'Faire un Don',                en: 'Make a Donation' },
  '/en/don':         { fr: 'Faire un Don',                en: 'Make a Donation' },

  // Contact
  '/fr/contact':     { fr: 'Contact',                     en: 'Contact' },
  '/en/contact':     { fr: 'Contact',                     en: 'Contact' },

  // Carrière
  '/fr/carriere':    { fr: 'Carrière',                    en: 'Career' },
  '/en/carriere':    { fr: 'Carrière',                    en: 'Career' },
};

// Patterns dynamiques (slug)
const DYNAMIC_PATTERNS = [
  { pattern: /^\/(fr|en)\/projet\/(.+)$/,         title: (m) => ({ fr: `Projet — ${m[2]}`,         en: `Project — ${m[2]}` }) },
  { pattern: /^\/(fr|en)\/actualite\/(.+)$/,      title: (m) => ({ fr: `Actualité — ${m[2]}`,      en: `News — ${m[2]}` }) },
  { pattern: /^\/(fr|en)\/publication\/(.+)$/,    title: (m) => ({ fr: `Publication — ${m[2]}`,    en: `Publication — ${m[2]}` }) },
  { pattern: /^\/(fr|en)\/full-description\/(.+)$/,title: (m) => ({ fr: `Action — ${m[2]}`,        en: `Action — ${m[2]}` }) },
  { pattern: /^\/(fr|en)\/info\/(.+)$/,           title: (m) => ({ fr: `Info — ${m[2]}`,           en: `Info — ${m[2]}` }) },
  { pattern: /^\/(fr|en)\/news\/(.+)$/,           title: (m) => ({ fr: `Actualité — ${m[2]}`,      en: `News — ${m[2]}` }) },
];

/**
 * Hook pour définir le titre de la page et envoyer un pageview à Google Analytics.
 * @param {string} [customTitle] - Titre personnalisé (optionnel, ex: titre d'un article depuis l'API)
 */
const usePageTitle = (customTitle) => {
  const location = useLocation();
  const path = location.pathname;

  useEffect(() => {
    const lang = path.startsWith('/en') ? 'en' : 'fr';
    let pageTitle = null;

    // 1. Titre personnalisé passé en paramètre
    if (customTitle) {
      pageTitle = customTitle;
    }
    // 2. Route exacte
    else if (PAGE_TITLES[path]) {
      pageTitle = PAGE_TITLES[path][lang];
    }
    // 3. Pattern dynamique
    else {
      for (const { pattern, title } of DYNAMIC_PATTERNS) {
        const match = path.match(pattern);
        if (match) {
          // Formater le slug: remplacer les tirets par des espaces et capitaliser
          const formatted = match[2]
            .split('-')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
          const m = [match[0], match[1], formatted];
          pageTitle = title(m)[lang];
          break;
        }
      }
    }

    // Définir le titre du document
    const fullTitle = pageTitle
      ? `${pageTitle} | ${BASE_TITLE}`
      : BASE_TITLE;

    document.title = fullTitle;

    // Envoyer le pageview à Google Analytics
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-24FK7VJHTJ', {
        page_title: fullTitle,
        page_path: path,
      });
    }
  }, [path, customTitle]);
};

export default usePageTitle;
